import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import CircularProgress from "@mui/material/CircularProgress";

import { colors } from "@dotnet/telematics-design-system";

import TableIsEmptyIcon from "assets/customIcons/emptyTable/TableIsEmptyIcon";
import {
    API_IS_BEING_CANCELLED,
    body1SemiBoldStyles,
    ENDPOINTS,
    useApi
} from "shared";

import Token from "./Token";

const TokenList = () => {
    const { gray700 } = colors;

    const { t } = useTranslation();
    const { getData, cancelSource, isCanceled, handleResponse } = useApi();

    const [isLoading, setIsLoading] = useState(true);
    const [tokens, setTokens] = useState([]);

    useEffect(() => {
        let isSubscribed = true;

        const getTokens = async () => {
            try {
                const { data } = await getData(ENDPOINTS.Tokens);

                if (isSubscribed) {
                    setTokens(data);
                }
            } catch (error) {
                if (isSubscribed && !isCanceled(error)) {
                    handleResponse(error);
                }
            }

            setIsLoading(false);
        };

        getTokens();

        return () => {
            isSubscribed = false;
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };

        // eslint-disable-next-line
    }, []);

    const removeToken = (id: number) =>
        setTokens(prevTokens =>
            prevTokens.filter((token: any) => token.id !== id)
        );

    const renderContent = () => {
        if (isLoading) {
            return (
                <div
                    css={css({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "92px"
                    })}
                >
                    <CircularProgress />
                </div>
            );
        }

        return tokens.length > 0 ? (
            <div
                css={css({
                    transition: "opacity 300ms ease-out"
                })}
            >
                {tokens.map((token: any, i: number) => (
                    <Token
                        key={token.id}
                        data={token}
                        isFirst={i === 0}
                        isLast={tokens.length === i + 1}
                        removeFromList={removeToken}
                    />
                ))}
            </div>
        ) : (
            <div
                css={css({
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    padding: "16px",

                    "& > svg": {
                        fontSize: "120px"
                    }
                })}
            >
                <TableIsEmptyIcon />

                <div css={[body1SemiBoldStyles, { margin: "16px 0 8px 0" }]}>
                    {t("Api tokens##no tokens")}
                </div>

                <div
                    css={css({
                        color: gray700,
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "0.1px"
                    })}
                >
                    {t("Api tokens##no tokens description")}
                </div>
            </div>
        );
    };

    return renderContent();
};

export default TokenList;
